import {
    Layout,
    Button,
    Form,
    FormLayout,
    TextField,
    LegacyCard, Spinner, EmptyState, IndexTable, useIndexResourceState, Link, Badge, InlineStack
} from '@shopify/polaris';

import {useState, useEffect, useCallback, useMemo} from 'react';
import Api from "../../helpers/api";
import Paginator from "../../components/Paginator";
import {useToast} from "@shopify/app-bridge-react";
import OrderDetailsModal from "./OrderDetailsModal";
import {formatDateToUsFormat} from "../Dates";
import {useLocation} from "react-router-dom";
import ShippingExportModal from "./ShippingExportModal";

const OrdersSearch = ({
    loading,
    setLoading,
    searchTitle,
    exportModalActive,
    setExportModalActive,
}) => {
    const [results, setResults] = useState([]);
    const [meta, setMeta] = useState({current_page: 1, last_page: 1, total: 0})
    const [errors, setErrors] = useState({})
    const toast = useToast();
    const [displayedOrder, setDisplayedOrder] = useState(null);

    const queryString = useQuery();
    const [query, setQuery] = useState(queryString.get('query') ?? '');

    function useQuery() {
        const { search } = useLocation();

        return useMemo(() => new URLSearchParams(search), [search]);
    }

    useEffect(() => {
        loadOrders();
    }, []);

    const handleSubmit = (search = null) => {
        loadOrders(1, search);
    }

    const loadOrders = useCallback((page = 1, search = null) => {
        setLoading(true);
        setDisplayedOrder(null);
        setResults([]);

        Api
            .findOrders({
                search: search ?? query,
                page: page,
            })
            .then((response) => {
                setErrors([]);
                setResults(response.data?.data ?? []);
                setMeta(response.data?.meta ?? {current_page: 1, last_page: 1, total: 0});
            })
            .catch(err => {
                if (err.response.status == 422) {
                    toast.show("Validation errors", {duration: 5000});
                    setErrors(
                        Object.keys(err.response.data.errors).reduce((prev, key) => {
                            prev[key] = err.response.data.errors[key][0];
                            return prev;
                        }, {}),
                    );
                } else if (err.response.status == 403) {
                    toast.show("You don't have permissions to view this page.", {duration: 5000});
                } else {
                    toast.show("Server error", {duration: 5000});
                }

            })
            .finally(() => setLoading(false));
    }, [query]);

    return <>
        <Layout.Section>
            <Form onSubmit={() => handleSubmit()}>
                <FormLayout>
                    <TextField
                        value={query}
                        autoComplete={'on'}
                        onChange={setQuery}
                        label={searchTitle ?? "Search orders by Order Number, Customer Name, Customer Email"}
                        type="text"
                        error={errors['search']}
                        connectedRight={<Button submit loading={loading}  variant="primary">Search</Button>}
                        clearButton
                        onClearButtonClick={() => {setQuery(''); handleSubmit('')}}
                    />
                </FormLayout>
            </Form>
        </Layout.Section>

        <Layout.Section>
            { !loading && results.length > 0 && (
                <Paginator currentPage={meta.current_page} lastPage={meta.last_page} show={true} callback={loadOrders} />
            ) }
            <LegacyCard>
                <IndexTable
                    emptyState={<EmptyState image={null}>There are no results matching your search.</EmptyState>}
                    loading={loading}
                    headings={[
                        {title: 'Order number'},
                        {title: 'Date'},
                        {title: 'Customer name'},
                        {title: 'Total'},
                    ]}
                    itemCount={meta.total}
                >
                    {results.map((order, index) => (<IndexTable.Row
                        id={order.id}
                        key={order.id}
                        position={index}
                    >
                        <IndexTable.Cell>
                            <Link removeUnderline onClick={() => setDisplayedOrder(order)}>{order.additional_data.name}</Link>
                        </IndexTable.Cell>
                        <IndexTable.Cell>{formatDateToUsFormat(order.shopify_created_at)}</IndexTable.Cell>
                        <IndexTable.Cell>{order.additional_data.customer?.first_name} {order.additional_data.customer?.last_name}</IndexTable.Cell>
                        <IndexTable.Cell>${order.additional_data.total_price}</IndexTable.Cell>
                    </IndexTable.Row>))}
                </IndexTable>
            </LegacyCard>

            { !loading && results.length > 0 && (
                <Paginator currentPage={meta.current_page} lastPage={meta.last_page} show={true} callback={loadOrders} />
            ) }
        </Layout.Section>
        <OrderDetailsModal
            order={displayedOrder}
            onClose={() => setDisplayedOrder(null)}
            onOrderSave={() => {
                loadOrders(meta.currentPage, query);
            }}
        />
    </>;
}

export default OrdersSearch;
